import { useState, useEffect } from 'react';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { preferencesAPI } from '@/lib/supabase/preferences';
import generatedFeatures from '@/features/docs/data/whats-new-generated.json';

// Local storage key
const STORAGE_KEY = 'lastSeenReleaseId';

// Release history key (stores last 5 releases)
const RELEASE_HISTORY_KEY = 'releaseHistory';

// Define features here - update when adding new features
export interface WhatsNewFeature {
  id: string;
  title: string;
  description: string;
  highlight?: boolean;
  releaseId: string; // Which release this feature belongs to
  commitDate?: string; // ISO date string (for auto-generated features)
}

// Legacy export for backward compatibility
export const FEATURES: WhatsNewFeature[] = [];
export const MANUAL_FEATURES: WhatsNewFeature[] = [];

// Interface for generated features JSON
interface GeneratedFeaturesData {
  generatedAt: string;
  lookbackDays: number;
  totalFound: number;
  features: WhatsNewFeature[];
}

// Get all features from auto-generated JSON
function getAllFeatures(): WhatsNewFeature[] {
  const generated = (generatedFeatures as GeneratedFeaturesData).features || [];
  return generated;
}

// Get the current (latest) release ID from auto-generated features
function getCurrentReleaseId(): string {
  const features = getAllFeatures();
  if (features.length > 0 && features[0]) {
    return features[0].releaseId;
  }
  // Fallback to today's date format
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `auto_${year}_${month}_${day}`;
}

// Release history interface
interface ReleaseHistory {
  releaseId: string;
  features: WhatsNewFeature[];
}

// eslint-disable-next-line max-lines-per-function
export const useWhatsNew = () => {
  const { user } = useAuth();
  const [showBadge, setShowBadge] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [releaseHistory, setReleaseHistory] = useState<ReleaseHistory[]>([]);

  // Get all features from auto-generated JSON
  const allFeatures = getAllFeatures();
  // Get the current (latest) release ID dynamically
  const currentReleaseId = getCurrentReleaseId();

  useEffect(() => {
    checkShouldShowBadge();
    loadReleaseHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const getStorageKey = () => {
    if (!user?.id) {
      return `${STORAGE_KEY}_anonymous`;
    }
    return `${STORAGE_KEY}_${user.id}`;
  };

  const getHistoryStorageKey = () => {
    if (!user?.id) {
      return `${RELEASE_HISTORY_KEY}_anonymous`;
    }
    return `${RELEASE_HISTORY_KEY}_${user.id}`;
  };

  const loadReleaseHistory = () => {
    try {
      // Build release history from all features
      const featuresByRelease = allFeatures.reduce(
        (acc, feature) => {
          const releaseId = feature.releaseId;
          if (!acc[releaseId]) {
            acc[releaseId] = [];
          }
          if (acc[releaseId]) {
            acc[releaseId].push(feature);
          }
          return acc;
        },
        {} as Record<string, WhatsNewFeature[]>
      );

      // Convert to array and sort by release ID (newest first)
      const history: ReleaseHistory[] = Object.entries(featuresByRelease)
        .map(([releaseId, features]) => ({ releaseId, features }))
        .sort((a, b) => {
          // Sort by release ID (v2_1 > v2_0, v3_0 > v2_1, etc.)
          // For auto-generated, sort by date prefix
          if (a.releaseId.startsWith('auto_') && b.releaseId.startsWith('auto_')) {
            return b.releaseId.localeCompare(a.releaseId);
          }
          // Manual releases come first
          if (a.releaseId.startsWith('auto_')) return 1;
          if (b.releaseId.startsWith('auto_')) return -1;
          return b.releaseId.localeCompare(a.releaseId);
        })
        .slice(0, 5); // Keep only last 5 releases

      setReleaseHistory(history);

      // Save to localStorage as backup
      const storageKey = getHistoryStorageKey();
      try {
        localStorage.setItem(storageKey, JSON.stringify(history));
      } catch {
        // Ignore localStorage errors (quota exceeded, private browsing, etc.)
      }
    } catch {
      // Initialize with current release on error
      const currentReleaseFeatures = allFeatures.filter(f => f.releaseId === currentReleaseId);
      if (currentReleaseFeatures.length > 0) {
        setReleaseHistory([{ releaseId: currentReleaseId, features: currentReleaseFeatures }]);
      }
    }
  };

  const checkShouldShowBadge = async () => {
    const storageKey = getStorageKey();

    try {
      // Check server preferences first (if logged in)
      if (user?.id) {
        try {
          const preferences = await preferencesAPI.getWhatsNewPreferences(user.id);
          const lastSeenId = preferences.lastSeenReleaseId || null;

          // Show badge if last seen ID doesn't match current release ID
          setShowBadge(lastSeenId !== currentReleaseId);
          setIsLoading(false);
          return;
        } catch {
          // Fall through to localStorage fallback
        }
      }

      // Fallback to localStorage
      const stored = localStorage.getItem(storageKey);

      if (stored) {
        const lastSeenId = stored;
        // Show badge if stored ID doesn't match current release ID
        setShowBadge(lastSeenId !== currentReleaseId);
      } else {
        // First time user - show badge
        setShowBadge(true);
      }
    } catch {
      // Default to showing badge if we can't determine state
      setShowBadge(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = async () => {
    const storageKey = getStorageKey();

    try {
      // Update server preferences if logged in
      if (user?.id) {
        try {
          await preferencesAPI.updateWhatsNewPreferences(user.id, currentReleaseId);
        } catch {
          // Fall through to localStorage
        }
      }

      // Update localStorage (always, as backup)
      localStorage.setItem(storageKey, currentReleaseId);

      // Reload release history
      loadReleaseHistory();

      // Hide badge
      setShowBadge(false);
      setIsPopoverOpen(false);
    } catch {
      // Still try to update localStorage
      try {
        localStorage.setItem(storageKey, currentReleaseId);
        setShowBadge(false);
        setIsPopoverOpen(false);
      } catch {
        // Ignore localStorage errors (quota exceeded, private browsing, etc.)
      }
    }
  };

  // Get features for current release (auto-generated from git commits)
  const currentFeatures = allFeatures.filter(f => f.releaseId === currentReleaseId);

  // Mark the first 2 features as highlights for visual prominence
  const featuresWithHighlights = currentFeatures.map((f, index) => ({
    ...f,
    highlight: index < 2
  }));

  return {
    showBadge,
    isPopoverOpen,
    setIsPopoverOpen,
    isLoading,
    releaseId: currentReleaseId,
    features: featuresWithHighlights,
    allFeatures, // All features for reference
    releaseHistory: releaseHistory.slice(0, 5), // Return last 5 releases
    onDismiss: handleDismiss
  };
};
